import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api.js';

// ── Types ───────────────────────────────────────────────────────────────────

interface SyncConfig {
  id: string;
  account_id: string;
  table_name: string;
  google_connection_id: string;
  spreadsheet_id: string;
  sheet_name: string;
  filters: string;
  sync_mode: string;
  columns: string;
  custom_query: string | null;
  is_enabled: number;
  schedule_type: string;
  schedule_time: string;
  date_range: string;
  auto_sync: number;
  last_sync_at: string | null;
  last_sync_status: string | null;
  last_sync_rows: number;
  last_sync_duration_ms: number | null;
  created_at: string;
}

interface TableInfo {
  name: string;
  dateColumn: string | null;
  hasAccountId: boolean;
  rowCount: number;
  minDate: string | null;
  maxDate: string | null;
  columns: string[];
}

interface GoogleConnection {
  id: string;
  name: string;
  service_account_email: string;
}

// ── Marketplace aliases ─────────────────────────────────────────────────────

const MARKETPLACE_LABELS: Record<string, string> = {
  ATVPDKIKX0DER: 'US',
  A2EUQ1WTGCTBG2: 'CA',
  A1AM78C64UM0Y8: 'MX',
  A1F83G8C2ARO7P: 'UK',
  A1PA6795UKMFR9: 'DE',
  A13V1IB3VIYZZH: 'FR',
  APJ6JRA9NG5V4: 'IT',
  A1RKKUPIHCS9HS: 'ES',
  A1805IZSGTT6HS: 'NL',
  A2NODRKZP88ZB9: 'SE',
  A1C3SOZRARQ6R3: 'PL',
  AMEN7PMS3EDWL: 'BE',
  A1F83G8C2ARO7P_IE: 'IE',
};

function mpLabel(mpId: string): string {
  return MARKETPLACE_LABELS[mpId] || mpId.slice(0, 8);
}

// ── Query templates ─────────────────────────────────────────────────────────

const QUERY_TEMPLATES: { name: string; description: string; query: string; sheet: string }[] = [
  {
    name: 'Traffic + Sales by ASIN',
    description: 'Daily sessions, page views, units, sales in local + USD, conversion rate',
    sheet: 'traffic_sales_asin',
    query: `SELECT t.date, t.asin,
       m.nickname AS market, lp.nickname AS product,
       t.sessions, t.page_views, t.buy_box_pct,
       t.units_ordered, t.ordered_product_sales,
       t.currency,
       ROUND(t.ordered_product_sales * COALESCE(er.rate_to_usd, 1), 2) AS sales_usd,
       t.unit_session_pct AS conversion_pct
FROM amazon_traffic_daily t
LEFT JOIN marketplaces m ON m.id = t.marketplace
LEFT JOIN (SELECT asin, marketplace, MIN(sku) AS sku FROM amazon_catalog WHERE sku NOT LIKE 'amzn.gr.%' GROUP BY asin, marketplace) c
  ON c.asin = t.asin AND c.marketplace = t.marketplace
LEFT JOIN amazon_products lp ON lp.sku = c.sku
LEFT JOIN exchange_rates er ON er.date = t.date AND er.currency = t.currency
WHERE t.date >= date('now', '-30 days')
ORDER BY t.date ASC, t.marketplace, t.sessions DESC`,
  },
  {
    name: 'Orders + COGS',
    description: 'Orders with product nicknames and COGS from purchase batches',
    sheet: 'orders_with_cogs',
    query: `SELECT o.purchase_date, o.sku, lp.nickname,
       o.marketplace, o.quantity, o.item_price, o.currency,
       lp.cogs AS unit_cogs,
       ROUND(lp.cogs * o.quantity, 2) AS total_cogs,
       ROUND(o.item_price - (lp.cogs * o.quantity), 2) AS gross_profit
FROM amazon_orders o
LEFT JOIN amazon_products lp ON lp.sku = o.sku
WHERE o.purchase_date >= date('now', '-30 days')
ORDER BY o.purchase_date DESC`,
  },
  {
    name: 'Monthly Sales Summary',
    description: 'Revenue, units, and order count per month per marketplace',
    sheet: 'monthly_sales',
    query: `SELECT strftime('%Y-%m', purchase_date) AS month,
       marketplace,
       COUNT(DISTINCT amazon_order_id) AS orders,
       SUM(quantity) AS units,
       ROUND(SUM(item_price), 2) AS revenue,
       currency
FROM amazon_orders
GROUP BY 1, 2, currency
ORDER BY 1 DESC, revenue DESC`,
  },
  {
    name: 'Inventory + Catalog',
    description: 'Current stock levels with product titles, COGS, and restock info',
    sheet: 'inventory_full',
    query: `SELECT c.sku, c.asin, lp.nickname, c.title, c.marketplace,
       i.qty_available, i.qty_inbound, i.qty_reserved,
       i.qty_unfulfillable, i.is_remote,
       lp.cogs AS unit_cogs,
       ROUND(lp.cogs * i.qty_available, 2) AS stock_value
FROM amazon_catalog c
LEFT JOIN amazon_inventory_current i
  ON i.sku = c.sku AND i.marketplace = c.marketplace AND i.account_id = c.account_id
LEFT JOIN amazon_products lp ON lp.sku = c.sku
WHERE i.qty_available IS NOT NULL
ORDER BY c.marketplace, c.sku`,
  },
  {
    name: 'Daily P&L',
    description: 'Daily sales, fees, and estimated profit per marketplace',
    sheet: 'daily_pnl',
    query: `SELECT f.date, f.marketplace, f.currency,
       f.product_sales, f.shipping_credits,
       f.fba_fees, f.commission, f.other_fees,
       f.refunds, f.net_proceeds,
       f.cogs,
       ROUND(f.net_proceeds - f.cogs, 2) AS est_profit
FROM amazon_financial_daily f
WHERE f.date >= date('now', '-30 days')
ORDER BY f.date DESC, f.marketplace`,
  },
  {
    name: 'Traffic + Feedback',
    description: 'Store traffic with sessions, conversion, and feedback per day',
    sheet: 'traffic_feedback',
    query: `SELECT t.date, t.marketplace,
       t.ordered_product_sales, t.units_ordered,
       t.sessions, t.page_views,
       t.buy_box_pct, t.unit_session_pct,
       t.feedback_received, t.negative_feedback
FROM amazon_traffic_store_daily t
WHERE t.date >= date('now', '-30 days')
ORDER BY t.date DESC, t.marketplace`,
  },
  {
    name: 'PPC Performance',
    description: 'Search terms with clicks, spend, sales, and ACoS',
    sheet: 'amazon_ppc_search_terms',
    query: `SELECT st.query AS search_term,
       st.campaign_name, st.ad_group_name,
       st.match_type, st.impressions, st.clicks,
       ROUND(st.cost, 2) AS spend,
       ROUND(st.sales, 2) AS sales,
       CASE WHEN st.sales > 0
         THEN ROUND(st.cost / st.sales * 100, 1)
         ELSE NULL END AS acos_pct,
       st.orders
FROM amazon_ppc_search_terms st
WHERE st.clicks > 0
ORDER BY st.cost DESC`,
  },
  {
    name: 'SKU Profitability',
    description: 'Per-SKU revenue, COGS, fees, and margin from monthly rollups',
    sheet: 'sku_profitability',
    query: `SELECT r.month, r.marketplace, r.sku,
       lp.nickname, r.asin,
       r.units_ordered, r.revenue,
       r.fba_fees, r.commission, r.refunds,
       r.cogs,
       ROUND(r.revenue - r.fba_fees - r.commission - r.refunds - COALESCE(r.cogs, 0), 2) AS net_profit,
       CASE WHEN r.revenue > 0
         THEN ROUND((r.revenue - r.fba_fees - r.commission - r.refunds - COALESCE(r.cogs, 0)) / r.revenue * 100, 1)
         ELSE NULL END AS margin_pct
FROM amazon_monthly_rollups r
LEFT JOIN amazon_products lp ON lp.sku = r.sku
ORDER BY r.month DESC, net_profit DESC`,
  },
  {
    name: 'Competitive Pricing',
    description: 'Latest competitive prices with your price vs Buy Box gap',
    sheet: 'competitive_prices',
    query: `SELECT cp.date, cp.marketplace, cp.sku, cp.asin,
       lp.nickname,
       cp.your_price, cp.buy_box_price, cp.lowest_price,
       cp.price_gap, cp.price_position, cp.currency
FROM amazon_competitive_prices cp
LEFT JOIN amazon_products lp ON lp.sku = cp.sku
WHERE cp.date = (SELECT MAX(date) FROM amazon_competitive_prices)
ORDER BY cp.marketplace, cp.sku`,
  },
  {
    name: 'Removal Tracking',
    description: 'FBA removals with destination addresses and status',
    sheet: 'fba_removals',
    query: `SELECT r.request_date, r.sku, lp.nickname,
       r.marketplace, r.order_type, r.order_status,
       r.qty_requested, r.qty_shipped, r.qty_disposed,
       r.ship_city, r.ship_state,
       r.removal_order_id, r.tracking_number
FROM amazon_fba_removals r
LEFT JOIN amazon_products lp ON lp.sku = r.sku
ORDER BY r.request_date DESC`,
  },
  {
    name: 'Storage Fees by ASIN',
    description: 'Monthly storage fees with product info',
    sheet: 'storage_fees',
    query: `SELECT s.month, s.marketplace, s.asin, s.sku,
       lp.nickname,
       s.avg_quantity_on_hand, s.volume_cubic_feet,
       s.estimated_base_fee_usd, s.estimated_surcharge_usd,
       ROUND(s.estimated_base_fee_usd + s.estimated_surcharge_usd, 2) AS total_fee_usd
FROM amazon_storage_fees_monthly s
LEFT JOIN amazon_products lp ON lp.sku = s.sku
ORDER BY s.month DESC, total_fee_usd DESC`,
  },
];

// ── Component ───────────────────────────────────────────────────────────────

export function SheetsSync() {
  const [configs, setConfigs] = useState<SyncConfig[]>([]);
  const [tables, setTables] = useState<TableInfo[]>([]);
  const [connections, setConnections] = useState<GoogleConnection[]>([]);
  const [allMarketplaces, setAllMarketplaces] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [formMode, setFormMode] = useState<'table' | 'sql'>('table');
  const [formTable, setFormTable] = useState('');
  const [formSpreadsheet, setFormSpreadsheet] = useState('');
  const [formSheet, setFormSheet] = useState('');
  const [formConnection, setFormConnection] = useState('');
  const [formStartDate, setFormStartDate] = useState('');
  const [formEndDate, setFormEndDate] = useState('');
  const [formMarketplaces, setFormMarketplaces] = useState<string[]>([]);
  const [formSyncMode, setFormSyncMode] = useState<'full_refresh' | 'append' | 'refresh_period'>('full_refresh');
  const [formColumns, setFormColumns] = useState<string[]>([]);
  const [formCustomQuery, setFormCustomQuery] = useState('');
  const [formAutoSync, setFormAutoSync] = useState(false);
  const [formDateRange, setFormDateRange] = useState('30');
  const [sheetTabs, setSheetTabs] = useState<string[]>([]);
  const [loadingTabs, setLoadingTabs] = useState(false);
  const [spreadsheetTitles, setSpreadsheetTitles] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  // SQL preview
  const [previewData, setPreviewData] = useState<{ columns: string[]; rows: Record<string, unknown>[]; total: number } | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const loadTabs = useCallback(async (spreadsheetId: string, connectionId: string) => {
    if (!spreadsheetId || spreadsheetId.length < 20 || !connectionId) return;
    setLoadingTabs(true);
    try {
      const res = await api.get<{ title: string; tabs: string[] }>(
        `/sheets/${connectionId}/spreadsheet-tabs?spreadsheet_id=${encodeURIComponent(spreadsheetId)}`,
      );
      setSheetTabs(res.tabs);
      if (res.title) {
        setSpreadsheetTitles(prev => ({ ...prev, [spreadsheetId]: res.title }));
      }
    } catch {
      setSheetTabs([]);
    } finally {
      setLoadingTabs(false);
    }
  }, []);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [cfgs, tbls, conns, accts] = await Promise.all([
        api.get<SyncConfig[]>('/sheets-sync/configs'),
        api.get<TableInfo[]>('/sheets-sync/tables'),
        api.get<{ connections: GoogleConnection[] }>('/sheets'),
        api.get<{ id: string; marketplace_ids: string }[]>('/accounts'),
      ]);
      setConfigs(cfgs);
      setTables(tbls);
      setConnections(conns.connections);
      const mpSet = new Set<string>();
      for (const a of accts) {
        const ids: string[] = JSON.parse(a.marketplace_ids || '[]');
        ids.forEach((id) => mpSet.add(id));
      }
      setAllMarketplaces([...mpSet]);
      if (conns.connections.length > 0 && !formConnection) setFormConnection(conns.connections[0].id);

      // Load spreadsheet titles for all configs
      const uniqueSheets = new Map<string, string>();
      for (const cfg of cfgs) {
        if (cfg.spreadsheet_id && cfg.google_connection_id) {
          uniqueSheets.set(cfg.spreadsheet_id, cfg.google_connection_id);
        }
      }
      for (const [ssId, connId] of uniqueSheets) {
        api.get<{ title: string; tabs: string[] }>(
          `/sheets/${connId}/spreadsheet-tabs?spreadsheet_id=${encodeURIComponent(ssId)}`,
        ).then(res => {
          if (res.title) setSpreadsheetTitles(prev => ({ ...prev, [ssId]: res.title }));
        }).catch(() => {});
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadData(); }, [loadData]);

  const handleSync = async (configId: string) => {
    setSyncing((prev) => ({ ...prev, [configId]: true }));
    try {
      await api.post(`/sheets-sync/configs/${configId}/sync`);
      await loadData();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSyncing((prev) => ({ ...prev, [configId]: false }));
    }
  };

  const handleDelete = async (configId: string) => {
    if (!confirm('Delete this sync config?')) return;
    try {
      await api.delete(`/sheets-sync/configs/${configId}`);
      await loadData();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const handleToggle = async (config: SyncConfig) => {
    try {
      await api.put(`/sheets-sync/configs/${config.id}`, {
        is_enabled: config.is_enabled ? 0 : 1,
      });
      await loadData();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const handleEdit = (config: SyncConfig) => {
    setEditId(config.id);
    setFormTable(config.table_name);
    setFormSpreadsheet(config.spreadsheet_id);
    setFormSheet(config.sheet_name);
    setFormConnection(config.google_connection_id);
    setFormSyncMode((config.sync_mode || 'full_refresh') as 'full_refresh' | 'append' | 'refresh_period');
    setFormColumns(JSON.parse(config.columns || '[]'));
    setFormCustomQuery(config.custom_query || '');
    setFormMode(config.custom_query ? 'sql' : 'table');
    setFormAutoSync(config.auto_sync === 1);
    setFormDateRange(config.date_range || '30');
    const filters = JSON.parse(config.filters || '{}');
    setFormStartDate(filters.startDate || '');
    setFormEndDate(filters.endDate || '');
    setFormMarketplaces(filters.marketplaceIds || []);
    setPreviewData(null);
    setPreviewError(null);
    setShowForm(true);
    loadTabs(config.spreadsheet_id, config.google_connection_id);
  };

  const resetForm = () => {
    setEditId(null);
    setFormMode('table');
    setFormTable('');
    setFormSpreadsheet('');
    setFormSheet('');
    setFormDateRange('30');
    setFormStartDate('');
    setFormEndDate('');
    setFormMarketplaces([]);
    setFormSyncMode('full_refresh');
    setFormColumns([]);
    setFormCustomQuery('');
    setFormAutoSync(false);
    setSheetTabs([]);
    setPreviewData(null);
    setPreviewError(null);
    setShowForm(false);
  };

  const handlePreview = async () => {
    if (!formCustomQuery.trim()) return;
    setPreviewLoading(true);
    setPreviewError(null);
    setPreviewData(null);
    try {
      const result = await api.post<{ columns: string[]; rows: Record<string, unknown>[]; total: number }>(
        '/sheets-sync/preview',
        { query: formCustomQuery },
      );
      setPreviewData(result);
    } catch (e) {
      setPreviewError(e instanceof Error ? e.message : String(e));
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSave = async () => {
    if (formMode === 'sql' && !formCustomQuery.trim()) {
      setError('SQL query is required in Custom SQL mode');
      return;
    }
    if (formMode === 'table' && !formTable) {
      setError('Please select a table');
      return;
    }
    if (!formSpreadsheet || !formSheet) {
      setError('Spreadsheet ID and sheet name are required');
      return;
    }
    setSaving(true);
    try {
      const filters: Record<string, unknown> = {};
      if (formMode === 'table') {
        if (formDateRange && formDateRange !== 'all') filters.dateRange = formDateRange;
        if (formStartDate) filters.startDate = formStartDate;
        if (formEndDate) filters.endDate = formEndDate;
        if (formMarketplaces.length > 0) filters.marketplaceIds = formMarketplaces;
      }

      if (editId) {
        await api.put(`/sheets-sync/configs/${editId}`, {
          sheet_name: formSheet,
          spreadsheet_id: formSpreadsheet,
          filters,
          sync_mode: formSyncMode,
          columns: formColumns,
          custom_query: formMode === 'sql' ? formCustomQuery : null,
          auto_sync: formAutoSync,
          date_range: formDateRange,
        });
      } else {
        await api.post('/sheets-sync/configs', {
          table_name: formMode === 'table' ? formTable : '_custom',
          google_connection_id: formConnection,
          spreadsheet_id: formSpreadsheet,
          sheet_name: formSheet,
          filters,
          sync_mode: formSyncMode,
          columns: formColumns,
          custom_query: formMode === 'sql' ? formCustomQuery : undefined,
          auto_sync: formAutoSync,
          date_range: formDateRange,
        });
      }
      resetForm();
      await loadData();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const selectedTableInfo = tables.find((t) => t.name === formTable);

  const timeAgo = (iso: string | null) => {
    if (!iso) return 'never';
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">D1 Sheets Sync</h2>
          <p className="text-sm text-gray-500 mt-1">Mirror D1 database tables to Google Sheets</p>
        </div>
        <button
          onClick={() => { resetForm(); setShowForm(true); }}
          className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 transition-colors"
        >
          + New Sync
        </button>
      </div>

      {/* Error banner */}
      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700 flex justify-between items-center">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700 font-medium">
            Dismiss
          </button>
        </div>
      )}

      {/* Configured syncs */}
      {configs.length === 0 && !showForm && (
        <div className="rounded-xl border border-gray-200 bg-white p-8 text-center">
          <div className="text-gray-400 text-4xl mb-3">&#x1f4ca;</div>
          <p className="text-gray-500">No sync configs yet. Click "New Sync" to mirror a D1 table to Google Sheets.</p>
        </div>
      )}

      <div className="grid gap-4">
        {configs.map((cfg) => {
          const parsedFilters = JSON.parse(cfg.filters || '{}');
          const cfgColumns: string[] = JSON.parse(cfg.columns || '[]');
          const isCustom = !!cfg.custom_query;
          const isSuccess = cfg.last_sync_status === 'success';
          const isError = cfg.last_sync_status?.startsWith('error');
          return (
            <div key={cfg.id} className="rounded-xl border border-gray-200 bg-white p-5">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3">
                    <h3 className="font-semibold text-gray-900 truncate">
                      {isCustom ? 'Custom SQL' : cfg.table_name}
                    </h3>
                    <span className="text-xs text-gray-400">&rarr;</span>
                    <span className="text-sm text-gray-600 truncate">{cfg.sheet_name}</span>
                    {!cfg.is_enabled && (
                      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-500">Disabled</span>
                    )}
                  </div>
                  <div className="mt-1 flex items-center gap-4 text-xs text-gray-400">
                    <a
                      href={`https://docs.google.com/spreadsheets/d/${cfg.spreadsheet_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-brand-600 hover:underline truncate max-w-[300px]"
                    >
                      {spreadsheetTitles[cfg.spreadsheet_id] || cfg.spreadsheet_id.slice(0, 12) + '...'}
                    </a>
                    <span className="text-gray-400">{cfg.sheet_name}</span>
                    {isCustom && (
                      <span className="rounded px-1.5 py-0.5 text-xs font-medium bg-purple-50 text-purple-600">SQL</span>
                    )}
                    {cfg.auto_sync === 1 && (
                      <span className="rounded px-1.5 py-0.5 text-xs font-medium bg-green-50 text-green-600">Auto</span>
                    )}
                    <span className={`rounded px-1.5 py-0.5 text-xs font-medium ${cfg.sync_mode === 'append' ? 'bg-blue-50 text-blue-600' : cfg.sync_mode === 'refresh_period' ? 'bg-amber-50 text-amber-600' : 'bg-gray-100 text-gray-600'}`}>
                      {cfg.sync_mode === 'append' ? 'Append' : cfg.sync_mode === 'refresh_period' ? 'Refresh Period' : 'Full Refresh'}
                    </span>
                    {parsedFilters.dateRange && <span>Range: {parsedFilters.dateRange}</span>}
                    {parsedFilters.marketplaceIds?.length > 0 && (
                      <span>Markets: {parsedFilters.marketplaceIds.map(mpLabel).join(', ')}</span>
                    )}
                    {cfgColumns.length > 0 && (
                      <span>{cfgColumns.length} columns</span>
                    )}
                  </div>
                  {isCustom && cfg.custom_query && (
                    <p className="mt-1 text-xs text-gray-400 font-mono truncate max-w-lg">{cfg.custom_query}</p>
                  )}
                  {/* Sync status row */}
                  <div className="mt-2 flex items-center gap-3 text-xs">
                    {cfg.last_sync_at ? (
                      <>
                        <span className={`inline-flex items-center gap-1 ${isSuccess ? 'text-green-600' : isError ? 'text-red-600' : 'text-gray-500'}`}>
                          <span className={`h-1.5 w-1.5 rounded-full ${isSuccess ? 'bg-green-500' : isError ? 'bg-red-500' : 'bg-gray-400'}`} />
                          {isSuccess ? 'Success' : isError ? 'Error' : cfg.last_sync_status}
                        </span>
                        <span className="text-gray-400">{cfg.last_sync_rows.toLocaleString()} rows</span>
                        <span className="text-gray-400">{timeAgo(cfg.last_sync_at)}</span>
                        {cfg.last_sync_duration_ms != null && (
                          <span className="text-gray-400">{(cfg.last_sync_duration_ms / 1000).toFixed(1)}s</span>
                        )}
                      </>
                    ) : (
                      <span className="text-gray-400">Never synced</span>
                    )}
                  </div>
                  {isError && cfg.last_sync_status && (
                    <p className="mt-1 text-xs text-red-500 truncate max-w-lg">{cfg.last_sync_status}</p>
                  )}
                </div>
                {/* Actions */}
                <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                  <button
                    onClick={() => handleToggle(cfg)}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${cfg.is_enabled ? 'bg-brand-600' : 'bg-gray-300'}`}
                    title={cfg.is_enabled ? 'Disable' : 'Enable'}
                  >
                    <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${cfg.is_enabled ? 'translate-x-4' : 'translate-x-0.5'}`} />
                  </button>
                  <button
                    onClick={() => handleSync(cfg.id)}
                    disabled={syncing[cfg.id]}
                    className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
                  >
                    {syncing[cfg.id] ? 'Syncing...' : 'Sync Now'}
                  </button>
                  <button
                    onClick={() => handleEdit(cfg)}
                    className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(cfg.id)}
                    className="rounded-lg border border-red-200 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Add / Edit form */}
      {showForm && (
        <div className="rounded-xl border border-gray-200 bg-white p-6 space-y-5">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">{editId ? 'Edit Sync Config' : 'New Sync Config'}</h3>
            {/* Mode toggle */}
            <div className="flex rounded-lg border border-gray-200 overflow-hidden">
              <button
                onClick={() => setFormMode('table')}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                  formMode === 'table' ? 'bg-brand-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                Table
              </button>
              <button
                onClick={() => setFormMode('sql')}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                  formMode === 'sql' ? 'bg-purple-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                Custom SQL
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Google Connection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Google Connection</label>
              <select
                value={formConnection}
                onChange={(e) => setFormConnection(e.target.value)}
                disabled={!!editId}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-100"
              >
                {connections.map((conn) => (
                  <option key={conn.id} value={conn.id}>{conn.name || conn.service_account_email}</option>
                ))}
              </select>
            </div>

            {/* Table — only in table mode */}
            {formMode === 'table' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">D1 Table</label>
                <select
                  value={formTable}
                  onChange={(e) => {
                    setFormTable(e.target.value);
                    setFormColumns([]);
                    if (!formSheet || formSheet === formTable) {
                      setFormSheet(e.target.value);
                    }
                  }}
                  disabled={!!editId}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-100"
                >
                  <option value="">Select a table...</option>
                  {tables.map((t) => (
                    <option key={t.name} value={t.name}>
                      {t.name} ({t.rowCount.toLocaleString()} rows)
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Spreadsheet ID */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Spreadsheet ID</label>
              <input
                type="text"
                value={formSpreadsheet}
                onChange={(e) => setFormSpreadsheet(e.target.value)}
                onBlur={() => loadTabs(formSpreadsheet, formConnection)}
                placeholder="e.g. 17gX5dBaOCKUQ_KjM8WWosO-lAJV7HfTH50-IpjBN0lc"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
            </div>

            {/* Sheet Tab */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sheet Tab {loadingTabs && <span className="text-gray-400 font-normal">(loading...)</span>}
              </label>
              {sheetTabs.length > 0 ? (
                <div className="space-y-2">
                  <select
                    value={sheetTabs.includes(formSheet) ? formSheet : '_new'}
                    onChange={(e) => {
                      if (e.target.value === '_new') setFormSheet('');
                      else setFormSheet(e.target.value);
                    }}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  >
                    {sheetTabs.map(tab => (
                      <option key={tab} value={tab}>{tab}</option>
                    ))}
                    <option value="_new">+ New tab...</option>
                  </select>
                  {!sheetTabs.includes(formSheet) && (
                    <input
                      type="text"
                      value={formSheet}
                      onChange={(e) => setFormSheet(e.target.value)}
                      placeholder="New tab name"
                      autoFocus
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    />
                  )}
                </div>
              ) : (
                <input
                  type="text"
                  value={formSheet}
                  onChange={(e) => setFormSheet(e.target.value)}
                  placeholder="Tab name (enter spreadsheet ID first)"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              )}
            </div>
          </div>

          {/* Custom SQL editor */}
          {formMode === 'sql' && (
            <div className="space-y-4">
              {/* Template picker */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Quick Templates</label>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
                  {QUERY_TEMPLATES.map((tpl) => (
                    <button
                      key={tpl.name}
                      onClick={() => {
                        setFormCustomQuery(tpl.query);
                        if (!formSheet) setFormSheet(tpl.sheet);
                        setPreviewData(null);
                        setPreviewError(null);
                      }}
                      className="rounded-lg border border-gray-200 px-3 py-2 text-left hover:border-purple-300 hover:bg-purple-50 transition-colors"
                    >
                      <div className="text-xs font-medium text-gray-800">{tpl.name}</div>
                      <div className="text-[10px] text-gray-400 mt-0.5 line-clamp-2">{tpl.description}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">SQL Query</label>
              <textarea
                value={formCustomQuery}
                onChange={(e) => { setFormCustomQuery(e.target.value); setPreviewData(null); setPreviewError(null); }}
                placeholder={`SELECT o.purchase_date, o.sku, o.marketplace,\n       o.quantity, o.item_price,\n       lp.cogs, lp.nickname\nFROM amazon_orders o\nLEFT JOIN amazon_products lp ON lp.sku = o.sku\nWHERE o.purchase_date >= date('now', '-30 days')\nORDER BY o.purchase_date DESC`}
                rows={8}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono resize-y"
              />
              <div className="flex items-center justify-between mt-2">
                <p className="text-xs text-gray-400">
                  Read-only SELECT queries only. Supports JOINs, CTEs, aggregates, subqueries.
                </p>
                <button
                  onClick={handlePreview}
                  disabled={previewLoading || !formCustomQuery.trim()}
                  className="rounded-lg border border-purple-300 bg-purple-50 px-3 py-1.5 text-xs font-medium text-purple-700 hover:bg-purple-100 disabled:opacity-50 transition-colors"
                >
                  {previewLoading ? 'Running...' : 'Preview (20 rows)'}
                </button>
              </div>

              {/* Preview error */}
              {previewError && (
                <div className="mt-2 rounded-lg bg-red-50 border border-red-200 p-3 text-xs text-red-600 font-mono">
                  {previewError}
                </div>
              )}

              {/* Preview results */}
              {previewData && (
                <div className="mt-3 rounded-lg border border-gray-200 overflow-hidden">
                  <div className="bg-gray-50 px-3 py-2 text-xs text-gray-500 flex justify-between">
                    <span>{previewData.columns.length} columns, {previewData.total} preview rows</span>
                  </div>
                  <div className="overflow-x-auto max-h-64">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          {previewData.columns.map((col) => (
                            <th key={col} className="px-3 py-1.5 text-left font-medium text-gray-600 whitespace-nowrap border-b">
                              {col}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {previewData.rows.map((row, i) => (
                          <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                            {previewData.columns.map((col) => (
                              <td key={col} className="px-3 py-1.5 whitespace-nowrap text-gray-700 max-w-[200px] truncate">
                                {row[col] === null ? <span className="text-gray-300">null</span> : String(row[col])}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              </div>
            </div>
          )}

          {/* Sync Mode */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Write Mode</label>
            <div className="flex gap-3">
              <button
                onClick={() => setFormSyncMode('full_refresh')}
                className={`rounded-lg px-4 py-2 text-sm font-medium border transition-colors ${
                  formSyncMode === 'full_refresh'
                    ? 'border-brand-600 bg-brand-50 text-brand-700'
                    : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
              >
                Full Refresh
              </button>
              <button
                onClick={() => setFormSyncMode('append')}
                className={`rounded-lg px-4 py-2 text-sm font-medium border transition-colors ${
                  formSyncMode === 'append'
                    ? 'border-brand-600 bg-brand-50 text-brand-700'
                    : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
              >
                Append
              </button>
              <button
                onClick={() => setFormSyncMode('refresh_period')}
                className={`rounded-lg px-4 py-2 text-sm font-medium border transition-colors ${
                  formSyncMode === 'refresh_period'
                    ? 'border-amber-600 bg-amber-50 text-amber-700'
                    : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
              >
                Refresh Period
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-1">
              {formSyncMode === 'full_refresh'
                ? 'Clears the sheet and writes all matching rows each sync.'
                : formSyncMode === 'refresh_period'
                ? 'Deletes rows within the date range, then writes fresh data. Keeps history outside the period.'
                : 'Appends only new rows since last sync (deduplicates by ID).'}
            </p>
          </div>

          {/* Auto-sync + Date Range */}
          <div className="flex flex-wrap items-start gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Sync Trigger</label>
              <div className="flex gap-3">
                <button
                  onClick={() => setFormAutoSync(false)}
                  className={`rounded-lg px-4 py-2 text-sm font-medium border transition-colors ${
                    !formAutoSync
                      ? 'border-brand-600 bg-brand-50 text-brand-700'
                      : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  Manual
                </button>
                <button
                  onClick={() => setFormAutoSync(true)}
                  className={`rounded-lg px-4 py-2 text-sm font-medium border transition-colors ${
                    formAutoSync
                      ? 'border-brand-600 bg-brand-50 text-brand-700'
                      : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  Auto
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-1">
                {formAutoSync
                  ? 'Syncs automatically when new data lands in the source table.'
                  : 'Only syncs when you click the Sync button.'}
              </p>
            </div>
          </div>

          {/* Table-mode filters */}
          {formMode === 'table' && (
            <>
              {/* Date range */}
              {selectedTableInfo?.dateColumn && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Date Range</label>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {['7d', '14d', '30d', '90d', 'all'].map((range) => (
                      <button
                        key={range}
                        onClick={() => { setFormDateRange(range); setFormStartDate(''); setFormEndDate(''); }}
                        className={`rounded-lg px-3 py-1.5 text-xs font-medium border transition-colors ${
                          formDateRange === range && !formStartDate
                            ? 'border-brand-600 bg-brand-50 text-brand-700'
                            : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        {range === 'all' ? 'All Time' : `Last ${range}`}
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-500">or custom:</span>
                    <input
                      type="date"
                      value={formStartDate}
                      onChange={(e) => { setFormStartDate(e.target.value); setFormDateRange(''); }}
                      className="rounded-lg border border-gray-300 px-2 py-1 text-sm"
                    />
                    <span className="text-gray-400">to</span>
                    <input
                      type="date"
                      value={formEndDate}
                      onChange={(e) => { setFormEndDate(e.target.value); setFormDateRange(''); }}
                      className="rounded-lg border border-gray-300 px-2 py-1 text-sm"
                    />
                  </div>
                </div>
              )}

              {/* Marketplace filter */}
              {selectedTableInfo?.hasAccountId && allMarketplaces.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Marketplace Filter (optional)</label>
                  <div className="flex flex-wrap gap-2">
                    {allMarketplaces.map((mpId) => (
                      <button
                        key={mpId}
                        onClick={() => {
                          setFormMarketplaces((prev) =>
                            prev.includes(mpId) ? prev.filter((m) => m !== mpId) : [...prev, mpId],
                          );
                        }}
                        className={`rounded-lg px-3 py-1.5 text-xs font-medium border transition-colors ${
                          formMarketplaces.includes(mpId)
                            ? 'border-brand-600 bg-brand-50 text-brand-700'
                            : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        {mpLabel(mpId)}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Column picker */}
              {selectedTableInfo && selectedTableInfo.columns.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">Columns</label>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setFormColumns([])}
                        className="text-xs text-brand-600 hover:underline"
                      >
                        All
                      </button>
                      <button
                        onClick={() => setFormColumns([...selectedTableInfo.columns])}
                        className="text-xs text-brand-600 hover:underline"
                      >
                        Select All
                      </button>
                      <button
                        onClick={() => setFormColumns([])}
                        className="text-xs text-gray-500 hover:underline"
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                  <p className="text-xs text-gray-400 mb-2">
                    {formColumns.length === 0
                      ? 'All columns will be exported.'
                      : `${formColumns.length} of ${selectedTableInfo.columns.length} columns selected.`}
                  </p>
                  <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto rounded-lg border border-gray-100 bg-gray-50 p-3">
                    {selectedTableInfo.columns.map((col) => {
                      const selected = formColumns.length === 0 || formColumns.includes(col);
                      return (
                        <button
                          key={col}
                          onClick={() => {
                            if (formColumns.length === 0) {
                              setFormColumns(selectedTableInfo.columns.filter((c) => c !== col));
                            } else {
                              setFormColumns((prev) =>
                                prev.includes(col) ? prev.filter((c) => c !== col) : [...prev, col],
                              );
                            }
                          }}
                          className={`rounded px-2 py-0.5 text-xs border transition-colors ${
                            selected
                              ? 'border-brand-500 bg-brand-50 text-brand-700'
                              : 'border-gray-200 bg-white text-gray-400 line-through'
                          }`}
                        >
                          {col}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Table preview */}
              {selectedTableInfo && (
                <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Table Preview: {selectedTableInfo.name}</h4>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Rows:</span>{' '}
                      <span className="font-medium">{selectedTableInfo.rowCount.toLocaleString()}</span>
                    </div>
                    {selectedTableInfo.dateColumn && (
                      <div>
                        <span className="text-gray-500">Date range:</span>{' '}
                        <span className="font-medium">
                          {selectedTableInfo.minDate?.slice(0, 10) ?? '?'} &mdash; {selectedTableInfo.maxDate?.slice(0, 10) ?? '?'}
                        </span>
                      </div>
                    )}
                    <div>
                      <span className="text-gray-500">Columns:</span>{' '}
                      <span className="font-medium">{selectedTableInfo.columns.length}</span>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Save / Cancel */}
          <div className="flex gap-3 pt-2">
            <button
              onClick={handleSave}
              disabled={saving}
              className="rounded-lg bg-brand-600 px-5 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50 transition-colors"
            >
              {saving ? 'Saving...' : editId ? 'Update' : 'Create Sync'}
            </button>
            <button
              onClick={resetForm}
              className="rounded-lg border border-gray-300 px-5 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
